import React, { useState, useEffect } from 'react';
import { X, Filter, Check, Building2, Shield, Clock, Search } from 'lucide-react';
import { Button } from '../ui/Button';
import { Checkbox } from '../ui/Checkbox';
import { Input } from '../ui/Input';

const AgentsFilterModal = ({ 
  isOpen, 
  onClose, 
  onApply,
  filters = {
    statuses: [],
    departments: [],
    roles: [],
    timezones: []
  },
  options = {
    statusOptions: [],
    departments: [],
    roles: [],
    timezones: []
  }
}) => {
  const [localFilters, setLocalFilters] = useState({
    statuses: [],
    departments: [],
    roles: [],
    timezones: []
  });
  const [searchQueries, setSearchQueries] = useState({
    status: '',
    department: '',
    role: '',
    timezone: ''
  });

  useEffect(() => {
    if (isOpen) {
      setLocalFilters({ ...filters });
      setSearchQueries({
        status: '',
        department: '',
        role: '',
        timezone: ''
      });
    }
  }, [isOpen, filters]);

  if (!isOpen) return null;

  const handleToggle = (filterType, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(v => v !== value)
        : [...prev[filterType], value]
    }));
  };

  const handleSelectAll = (filterType, allValues) => {
    const currentValues = localFilters[filterType];
    if (currentValues.length === allValues.length) {
      setLocalFilters(prev => ({ ...prev, [filterType]: [] }));
    } else {
      setLocalFilters(prev => ({ ...prev, [filterType]: allValues }));
    }
  };

  const handleClearAll = (filterType) => {
    setLocalFilters(prev => ({ ...prev, [filterType]: [] }));
  };

  const handleClearAllFilters = () => {
    setLocalFilters({
      statuses: [],
      departments: [],
      roles: [],
      timezones: []
    });
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleReset = () => {
    setLocalFilters({ ...filters });
  };

  const handleSearchChange = (filterType, value) => {
    setSearchQueries(prev => ({ ...prev, [filterType]: value }));
  };

  // Filter options based on search
  const getFilteredOptions = (filterType, options) => {
    const searchQuery = searchQueries[filterType]?.toLowerCase() || '';
    if (!searchQuery) return options;

    return options.filter(opt => {
      const label = opt.label || opt.name || '';
      const value = opt.value || opt.id || '';
      const description = opt.description || '';
      return (
        label.toLowerCase().includes(searchQuery) ||
        value.toLowerCase().includes(searchQuery) ||
        description.toLowerCase().includes(searchQuery)
      );
    });
  };

  // Group timezones by region
  const groupTimezones = (timezones) => {
    const grouped = {
      'Americas': [],
      'Europe': [],
      'Asia': [],
      'Australia': []
    };
    
    timezones.forEach(tz => {
      if (tz.value?.startsWith('America/')) {
        grouped['Americas'].push(tz);
      } else if (tz.value?.startsWith('Europe/')) {
        grouped['Europe'].push(tz);
      } else if (tz.value?.startsWith('Asia/')) {
        grouped['Asia'].push(tz);
      } else if (tz.value?.startsWith('Australia/')) {
        grouped['Australia'].push(tz);
      }
    });
    
    return grouped;
  };

  const totalSelected = 
    localFilters.statuses.length +
    localFilters.departments.length +
    localFilters.roles.length +
    localFilters.timezones.length;

  const hasChanges = JSON.stringify(localFilters) !== JSON.stringify(filters);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Filter className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Filter Agents
              </h2>
              <p className="text-sm text-gray-500">
                Select filters to refine your agent list
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {totalSelected > 0 && (
              <span className="px-3 py-1 bg-blue-600 text-white rounded-full text-sm font-medium">
                {totalSelected} selected
              </span>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 flex-1 overflow-y-auto">
          {/* Status Filter Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Filter className="h-4 w-4 text-blue-600" />
                Status
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSelectAll('statuses', options.statusOptions.map(opt => opt.value))}
                  className="text-xs"
                >
                  {localFilters.statuses.length === options.statusOptions.length ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleClearAll('statuses')}
                  className="text-xs text-red-600 hover:text-red-700"
                  disabled={localFilters.statuses.length === 0}
                >
                  Clear
                </Button>
              </div>
            </div>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search statuses..."
                value={searchQueries.status}
                onChange={(e) => handleSearchChange('status', e.target.value)}
                className="pl-10 mb-3"
              />
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
              {getFilteredOptions('status', options.statusOptions).map((status) => {
                const isSelected = localFilters.statuses.includes(status.value);
                return (
                  <div
                    key={status.value}
                    className={`
                      flex items-center justify-between p-2 rounded-lg border-2 cursor-pointer transition-all text-sm
                      ${isSelected 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                    onClick={() => handleToggle('statuses', status.value)}
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleToggle('statuses', status.value)}
                        className="pointer-events-none"
                      />
                      <span className="font-medium">{status.label}</span>
                    </div>
                    {isSelected && <Check className="h-4 w-4 text-blue-600" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Department Filter Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-blue-600" />
                Department
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSelectAll('departments', options.departments.map(d => d.id || d.name))}
                  className="text-xs"
                >
                  {localFilters.departments.length === options.departments.length ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleClearAll('departments')}
                  className="text-xs text-red-600 hover:text-red-700"
                  disabled={localFilters.departments.length === 0}
                >
                  Clear
                </Button>
              </div>
            </div>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search departments..."
                value={searchQueries.department}
                onChange={(e) => handleSearchChange('department', e.target.value)}
                className="pl-10 mb-3"
              />
            </div>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {getFilteredOptions('department', options.departments).map((dept) => {
                const deptValue = dept.id || dept.name;
                const isSelected = localFilters.departments.includes(deptValue);
                return (
                  <div
                    key={deptValue}
                    className={`
                      flex items-center justify-between p-2 rounded-lg border-2 cursor-pointer transition-all
                      ${isSelected 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                    onClick={() => handleToggle('departments', deptValue)}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleToggle('departments', deptValue)}
                        className="pointer-events-none"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{dept.name}</p>
                        {dept.description && (
                          <p className="text-xs text-gray-500">{dept.description}</p>
                        )}
                      </div>
                    </div>
                    {isSelected && <Check className="h-4 w-4 text-blue-600" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Role Filter Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Shield className="h-4 w-4 text-blue-600" />
                Role
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSelectAll('roles', options.roles.map(r => r.id || r.name))}
                  className="text-xs"
                >
                  {localFilters.roles.length === options.roles.length ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleClearAll('roles')}
                  className="text-xs text-red-600 hover:text-red-700"
                  disabled={localFilters.roles.length === 0}
                >
                  Clear
                </Button>
              </div>
            </div>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search roles..."
                value={searchQueries.role}
                onChange={(e) => handleSearchChange('role', e.target.value)}
                className="pl-10 mb-3"
              />
            </div>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {getFilteredOptions('role', options.roles).map((role) => {
                const roleValue = role.id || role.name;
                const isSelected = localFilters.roles.includes(roleValue);
                return (
                  <div
                    key={roleValue}
                    className={`
                      flex items-center justify-between p-2 rounded-lg border-2 cursor-pointer transition-all
                      ${isSelected 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                    onClick={() => handleToggle('roles', roleValue)}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleToggle('roles', roleValue)}
                        className="pointer-events-none"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{role.name}</p>
                        {role.description && (
                          <p className="text-xs text-gray-500">{role.description}</p>
                        )}
                      </div>
                    </div>
                    {isSelected && <Check className="h-4 w-4 text-blue-600" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Timezone Filter Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-600" />
                Timezone
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSelectAll('timezones', options.timezones.map(t => t.value))}
                  className="text-xs"
                >
                  {localFilters.timezones.length === options.timezones.length ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleClearAll('timezones')}
                  className="text-xs text-red-600 hover:text-red-700"
                  disabled={localFilters.timezones.length === 0}
                >
                  Clear
                </Button>
              </div>
            </div>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search timezones..."
                value={searchQueries.timezone}
                onChange={(e) => handleSearchChange('timezone', e.target.value)}
                className="pl-10 mb-3"
              />
            </div>
            <div className="space-y-3 max-h-32 overflow-y-auto">
              {Object.entries(groupTimezones(getFilteredOptions('timezone', options.timezones))).map(([region, tzs]) => 
                tzs.length > 0 && (
                  <div key={region}>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">{region}</p>
                    <div className="space-y-2">
                      {tzs.map((tz) => {
                        const isSelected = localFilters.timezones.includes(tz.value);
                        return (
                          <div
                            key={tz.value}
                            className={`
                              flex items-center justify-between p-2 rounded-lg border-2 cursor-pointer transition-all
                              ${isSelected 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-gray-200 hover:border-gray-300'
                              }
                            `}
                            onClick={() => handleToggle('timezones', tz.value)}
                          >
                            <div className="flex items-center gap-2 flex-1">
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleToggle('timezones', tz.value)}
                                className="pointer-events-none"
                              />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{tz.label}</p>
                                <p className="text-xs text-gray-500">UTC{tz.offset}</p>
                              </div>
                            </div>
                            {isSelected && <Check className="h-4 w-4 text-blue-600" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAllFilters}
              className="text-red-600 hover:text-red-700"
              disabled={totalSelected === 0}
            >
              Clear All Filters
            </Button>
            {totalSelected > 0 && (
              <span className="text-sm text-gray-600">
                {totalSelected} filter{totalSelected !== 1 ? 's' : ''} active
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={!hasChanges}
            >
              Reset
            </Button>
            <Button
              onClick={handleApply}
              disabled={totalSelected === 0}
            >
              Apply Filters
              {totalSelected > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                  {totalSelected}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentsFilterModal;

